import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyBids = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [deleting, setDeleting] = useState(null);

  const effectiveRole = user?.active_role || user?.role;

  useEffect(() => {
    if (effectiveRole !== 'provider') {
      navigate('/dashboard');
      return;
    }
    fetchBids();
  }, [effectiveRole, navigate]);

  const fetchBids = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bids/my`);
      setBids(response.data);
    } catch (error) {
      console.error('Error fetching bids:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBid = async (bidId) => {
    if (!window.confirm('Are you sure you want to delete this bid?')) return;
    
    setDeleting(bidId);
    try {
      await axios.delete(`${API}/bids/${bidId}`);
      setBids(bids.filter(b => b.id !== bidId));
    } catch (error) {
      console.error('Error deleting bid:', error);
      alert(error.response?.data?.detail || 'Failed to delete bid');
    } finally {
      setDeleting(null);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to delete ALL your bids? This cannot be undone.')) return;
    
    setDeleting('all');
    try {
      for (const bid of bids) {
        await axios.delete(`${API}/bids/${bid.id}`);
      }
      setBids([]);
    } catch (error) {
      console.error('Error clearing bids:', error);
      fetchBids();
    } finally {
      setDeleting(null);
    }
  };

  const filteredBids = bids.filter(bid => {
    if (filter === 'all') return true;
    return bid.status === filter;
  });

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      accepted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      withdrawn: 'bg-gray-100 text-gray-700'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: '⏳',
      accepted: '✅',
      rejected: '❌',
      withdrawn: '↩️'
    };
    return icons[status] || '📝';
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { PHP: '₱', USD: '$', EUR: '€', GBP: '£' };
    return symbols[currency] || currency;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const stats = {
    total: bids.length,
    pending: bids.filter(b => b.status === 'pending').length,
    accepted: bids.filter(b => b.status === 'accepted').length,
    rejected: bids.filter(b => b.status === 'rejected').length
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="my-bids-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
              {t('myBids.title')}
            </h1>
            <p className="text-gray-600 mt-2">{t('myBids.subtitle')}</p>
          </div>
          <div className="flex gap-3">
            {bids.length > 0 && (
              <button
                onClick={handleClearAll}
                disabled={deleting === 'all'}
                className="text-red-500 hover:text-red-700 font-medium text-sm px-4 py-2 border border-red-300 rounded-lg hover:bg-red-50"
                data-testid="clear-all-bids-btn"
              >
                {deleting === 'all' ? t('myBids.clearing') : t('myBids.clearAll')}
              </button>
            )}
            <Link
              to="/jobs"
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg hover:shadow-lg font-semibold"
              data-testid="browse-jobs-btn"
            >
              {t('myBids.browseJobs')}
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-md p-4" data-testid="stat-total">
            <p className="text-gray-500 text-sm">{t('myBids.totalBids')}</p>
            <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4" data-testid="stat-pending">
            <p className="text-gray-500 text-sm">⏳ {t('myBids.pending')}</p>
            <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4" data-testid="stat-accepted">
            <p className="text-gray-500 text-sm">✅ {t('myBids.accepted')}</p>
            <p className="text-3xl font-bold text-green-600">{stats.accepted}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4" data-testid="stat-rejected">
            <p className="text-gray-500 text-sm">❌ {t('myBids.rejected')}</p>
            <p className="text-3xl font-bold text-red-600">{stats.rejected}</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6 p-2 flex gap-2 overflow-x-auto" data-testid="bid-filters">
          {[
            { id: 'all', label: `${t('myBids.all')} (${stats.total})` },
            { id: 'pending', label: `⏳ ${t('myBids.pending')} (${stats.pending})` },
            { id: 'accepted', label: `✅ ${t('myBids.accepted')} (${stats.accepted})` },
            { id: 'rejected', label: `❌ ${t('myBids.rejected')} (${stats.rejected})` },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
                filter === tab.id 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid={`filter-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Bids List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('myBids.loading')}</p>
          </div>
        ) : filteredBids.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="text-6xl mb-4">💼</div>
            <h3 className="text-xl font-bold text-gray-700 mb-2">
              {filter === 'all' ? t('myBids.noBids') : t('myBids.noBidsFiltered', { filter })}
            </h3>
            <p className="text-gray-500 mb-6">
              {filter === 'all' 
                ? t('myBids.noBidsHint')
                : t('myBids.tryDifferentFilter')}
            </p>
            {filter === 'all' && (
              <Link
                to="/jobs"
                className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-lg hover:shadow-lg font-semibold inline-block"
              >
                {t('myBids.browseAvailable')}
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBids.map((bid) => (
              <div
                key={bid.id}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition"
                data-testid={`bid-card-${bid.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{getStatusIcon(bid.status)}</span>
                      <h3 className="text-xl font-bold text-gray-800">{bid.job_title}</h3>
                      {getStatusBadge(bid.status)}
                    </div>
                    
                    <p className="text-gray-600 mb-4 line-clamp-2">{bid.message}</p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        💰 {t('myBids.bid')}: <strong className="text-blue-600">{getCurrencySymbol(bid.currency)}{bid.bid_amount?.toLocaleString()}</strong>
                      </span>
                      {bid.estimated_duration && (
                        <span className="flex items-center gap-1">
                          ⏱️ {t('myBids.duration')}: {bid.estimated_duration}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        📅 {t('myBids.submitted')}: {formatDate(bid.created_at)}
                      </span>
                    </div>
                  </div>

                  <div className="ml-6 flex flex-col items-end gap-2">
                    <button
                      onClick={() => handleDeleteBid(bid.id)}
                      disabled={deleting === bid.id}
                      className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition"
                      title={t('myBids.deleteBid')}
                      data-testid={`delete-bid-${bid.id}`}
                    >
                      {deleting === bid.id ? (
                        <div className="animate-spin h-5 w-5 border-2 border-red-500 border-t-transparent rounded-full"></div>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>

                    {bid.status === 'accepted' && (
                      <Link
                        to="/dashboard"
                        className="text-green-600 hover:text-green-800 text-sm font-medium"
                      >
                        {t('myBids.viewInDashboard')} →
                      </Link>
                    )}
                  </div>
                </div>

                {/* Status-specific messages */}
                {bid.status === 'accepted' && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-700 text-sm">
                      🎉 <strong>{t('myBids.congrats')}</strong> {t('myBids.acceptedMsg')}
                    </p>
                  </div>
                )}
                {bid.status === 'rejected' && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700 text-sm">
                      {t('myBids.rejectedMsg')}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Tips Section */}
        <div className="mt-8 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-6">
          <h3 className="font-bold text-gray-800 mb-4">{t('myBids.tipsTitle')}</h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-green-500">✓</span>
              {t('myBids.tip1')}
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500">✓</span>
              {t('myBids.tip2')}
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500">✓</span>
              {t('myBids.tip3')}
            </li>
          </ul>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MyBids;
